import { Link } from "@tanstack/react-router";
import { Link as LinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { DashboardConnectionsStripItem } from "@repo/shared";
import { cn } from "@/lib/utils";

const STATUS_DOT: Record<DashboardConnectionsStripItem["status"], string> = {
  active: "bg-status-success",
  idle: "bg-status-neutral",
  error: "bg-status-danger",
};

export function ConnectionsStrip({ items }: { items: DashboardConnectionsStripItem[] }) {
  const { t } = useTranslation("dashboard");

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-hairline pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[14px] font-semibold text-ink">
            {t("connectionsStrip.title", { defaultValue: "Connections" })}
          </h2>
          <span className="text-[12px] text-faint">
            {t("connectionsStrip.count", {
              count: items.length,
              defaultValue: `${items.length} tenants`,
            })}
          </span>
        </div>
        <Link
          to="/connections"
          search={{ q: undefined }}
          className="text-[12px] font-medium text-brand-text"
        >
          {t("connectionsStrip.manage", { defaultValue: "Manage →" })}
        </Link>
      </div>

      {items.length === 0 ? (
        <Link
          to="/connections"
          search={{ q: undefined }}
          className="flex items-center justify-center rounded-input border border-hairline border-dashed py-6 text-[12px] text-dim mt-3"
        >
          {t("connectionsStrip.empty", { defaultValue: "No connections — add one to start." })}
        </Link>
      ) : (
        <div className="grid gap-2 pt-3 md:grid-cols-3">
          {items.map((c) => (
            <Link
              key={c.id}
              to="/single-app/$connectionId/apps"
              params={{ connectionId: c.id }}
              search={{
                q: undefined,
                spaces: undefined,
                owners: undefined,
                status: undefined,
                access: undefined,
              }}
              className="flex items-center gap-3 rounded-input border border-hairline px-4 py-2.5 text-[13px] transition-colors hover:bg-muted"
            >
              <LinkIcon className="h-3.5 w-3.5 text-dim" strokeWidth={1.8} />
              <span className="flex-1 truncate font-medium text-ink">{c.name}</span>
              <span className={cn("h-2 w-2 rounded-full", STATUS_DOT[c.status])} aria-hidden />
              <span className="sr-only">{t(`connectionsStrip.status.${c.status}`)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
